// Focus-completion mutation sequence, extracted from TodayScreen so it is
// unit-testable WITHOUT rendering the 3106-line component (RTL full-render would
// be brittle).
//
// Bug B2/B3/B6 (2026-05-31 hunt): the inline version wrapped each write in
// `.catch(() => null)`, so a failed block/task completion was swallowed — the
// caller then fired notify*/refresh + the celebration as if it had succeeded,
// leaving a split state (task done, block open) with no error shown. This helper
// does NOT swallow: any failed write surfaces as an `error` result, and the
// caller fires the success path (notifiers, celebration, refresh) ONLY on `ok`.

export interface ScheduleBlockLike {
  id: string;
  task_id?: string | null;
  is_completed?: boolean;
}

export interface CompleteFocusDeps {
  completeScheduleBlock: (id: string) => Promise<unknown>;
  // Narrowed to the only patch this helper sends, so the real `updateTask`
  // (whose body is a wider Partial<Task>) is assignable here under
  // strictFunctionTypes.
  updateTask: (id: string, patch: { status: "completed" }) => Promise<unknown>;
}

export type CompleteFocusResult =
  | { status: "ok" }
  | { status: "error"; error: unknown };

/**
 * Complete a focus item (a schedule block, or a task, possibly with a linked
 * block). Returns `ok` only if every required write succeeded; otherwise returns
 * `error` carrying the original failure so the caller can surface it. Never
 * swallows — that is the whole point of the fix.
 */
export async function completeFocusItem(
  focusId: string,
  scheduleBlocks: readonly ScheduleBlockLike[],
  deps: CompleteFocusDeps
): Promise<CompleteFocusResult> {
  const isBlock = scheduleBlocks.some((b) => b.id === focusId);
  const linkedBlock = scheduleBlocks.find(
    (b) => (b.task_id === focusId || b.id === focusId) && !b.is_completed
  );

  try {
    if (isBlock && linkedBlock) {
      // A schedule block (possibly carrying a task).
      await deps.completeScheduleBlock(linkedBlock.id);
      if (linkedBlock.task_id) {
        await deps.updateTask(linkedBlock.task_id, { status: "completed" });
      }
    } else {
      // A task id, possibly with a linked block to close too.
      await deps.updateTask(focusId, { status: "completed" });
      if (linkedBlock) {
        await deps.completeScheduleBlock(linkedBlock.id);
      }
    }
    return { status: "ok" };
  } catch (error) {
    return { status: "error", error };
  }
}
